import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connectHits, connectStateResults } from 'react-instantsearch-dom';
import { adjustCart, addToCart, findDupesInArrayByCount } from 'ck-utils';
import { Loading, Typography } from 'ck-ui';
import { trackProductImpressions } from 'ck-utils/algoliaAnalytics';
import { Label } from '../../utils';
import { BundleLookPageProvider } from '../../context';
import { CuratedProductList } from './CuratedProductList';

// M3 has so many categories, we only want to group product into the main ones and follows an order or outfit top to bottom
const CATEGORY_PRIORITY = [
  'headwear',
  'tops',
  'bottoms',
  'footwear',
  'accessories',
  'living',
  'jewellery',
  // 'activewear'
];

const CuratedLookContainer = ({
  listName,
  featureImage,
  isSearchStalled,
  searchResults,
  hits
}) => {
  const { nbHits, queryID, hitsPerPage, page, index: indexName, hits: sHits } = searchResults || {};
  const [cart, setCart] = useState(null);

  // Fire product impression tracking on when query ID changes
  useEffect(() => {
    if (queryID) {
      trackProductImpressions(sHits, hitsPerPage, page, listName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryID]);

  // Listen to global cart change and update the state
  useEffect(() => {
    const handleSetCart = e => {
      setCart(e.detail.cart);
    };
    window.addEventListener('afterGetCart', handleSetCart);
    window.ShopifyAPI.getCart();
    return () => {
      window.removeEventListener('afterGetCart', handleSetCart);
    };
  }, []);

  // Render Loading
  if (isSearchStalled && !nbHits) {
    return <Loading isLoading />;
  }

  // Render No results
  if (!nbHits) {
    return (
      <div>
        <Typography renderAs="h1" display="inline" variant="heading4">
          Sorry, we couldn't find any products
        </Typography>
      </div>
    );
  }

  /**
   * Group the hit based on top level categories defined in the constant at the top
   * @type {Object} hits assigned to each group
   */
  const groupedHits = hits.reduce((result, hit) => hit['menu.categories'].reduce((acc, category) => {
    if (CATEGORY_PRIORITY.indexOf(category) === -1) return acc;

    const groupHits = acc[category] || [];
    return {
      ...acc,
      [category]: [...groupHits, hit]
    };
  }, result), {});

  // Don't render if the above is empty object
  if (!cart || Object.keys(groupedHits).length === 0) return <Loading isLoading />;

  /**
   * Group data to be passed into the page context
   * @type {{groupsHits: [], groupsSelectedVariants: [], icon: [], name: [], tag: []}}
   */
  const lookGroupData = CATEGORY_PRIORITY.reduce((acc, category, index) => {
    const groupHits = groupedHits[category];
    if (!groupHits) return acc;

    // Group gender priority, mens > womens > kids (rest)
    const groupGender = groupHits.reduce((gender, hit) => {
      if (gender === 'mens' || hit.gender.includes('mens')) return 'mens';
      if (gender === 'womens' || hit.gender.includes('womens')) return 'womens';
      return hit.gender[0];
    }, '');

    // Get sub categories of each product and see if we can find common subCategories that can be used to populate Icons and Group names
    const groupSubCategories = groupHits.reduce((subCategories, hit) => [...subCategories, ...new Set(hit.subCategory.split(','))], []);
    const commonSubCategories = groupHits.length > 1 ? findDupesInArrayByCount(groupSubCategories, groupHits.length) : groupSubCategories;

    let icon = '';
    let name = '';

    // Check Subcategories first to get icons and names
    if (commonSubCategories.length > 0) {
      icon = commonSubCategories.reduce((prev, subCategory) => Label.getIconCode(subCategory, groupGender) || prev, icon);
      name = commonSubCategories.reduce((prev, subCategory) => {
        const categoryName = Label.getLocaleString(subCategory, 'subcategory');
        if (prev && prev.length <= categoryName.length) return prev;
        return categoryName;
      }, name);
    }

    // Populate Icon and Name from main category if sub categories do not have Icon/Name mapping
    icon = icon || Label.getIconCode(category, groupGender);
    name = name || Label.getLocaleString(category, 'category');

    // Get products/variants in the look that are already added to cart
    const selectedVariants = cart && cart.items.reduce((prev, item) => {
      const selectedHit = groupHits.find(hit => String(item.product_id) === hit.objectID.replace('gid://shopify/Product/', ''));
      if (!selectedHit) return prev;
      return [
        ...prev,
        ...[...Array(item.quantity)].map(() => ({
          variantId: String(item.variant_id),
          imageUrl: selectedHit.image,
          vendor: selectedHit.vendor,
          title: selectedHit.title,
          shortTitle: selectedHit.shortTitle,
          price: selectedHit.price
        }))
      ];
    }, []);

    return {
      icon: [...acc.icon, icon],
      name: [...acc.name, name],
      tag: [...acc.tag, `${category}_${index}`],
      groupsHits: [...acc.groupsHits, groupHits],
      groupsSelectedVariants: [...acc.groupsSelectedVariants, selectedVariants],
      hasMultiProductsPerGroup: acc.hasMultiProductsPerGroup || groupHits.length > 1
    };
  }, { icon: [], name: [], tag: [], groupsHits: [], groupsSelectedVariants: [] });

  const { groupsHits, hasMultiProductsPerGroup, ...contextData } = lookGroupData;

  // Add to cart. TODO: LOADING STATE?
  const onAddToCart = (variantId) => {
    addToCart(variantId, 1, window.ajaxCart.loadCartToUpdateCartCount);
  };

  // Remove from Cart. TODO: LOADING STATE?
  const onRemoveFromCart = (variantId) => {
    const variant = cart.items.find(item => String(item.variant_id) === variantId);
    if (variantId) {
      adjustCart(variantId, variant.quantity - 1, window.ajaxCart.loadCartToUpdateCartCount);
    }
  };

  return (
    <BundleLookPageProvider byoBundle={contextData} listName={listName} onAddToCart={onAddToCart} onRemoveFromCart={onRemoveFromCart}>
      <CuratedProductList
        indexName={indexName}
        queryID={queryID}
        listName={listName}
        featureImage={featureImage}
        groupsHits={groupsHits}
        groupNames={lookGroupData.name}
        groupIcons={lookGroupData.icon}
        hasMultiProductsPerGroup={hasMultiProductsPerGroup}
      />
    </BundleLookPageProvider>
  );
};

CuratedLookContainer.propTypes = {
  featureImage: PropTypes.string.isRequired,
  hits: PropTypes.array,
  isSearchStalled: PropTypes.bool.isRequired,
  listName: PropTypes.string.isRequired,
  searchResults: PropTypes.object
};

CuratedLookContainer.defaultProps = {
  hits: [],
  searchResults: null
};

const CuratedLook = connectHits(connectStateResults(CuratedLookContainer));
export { CuratedLook };

export default CuratedLook;
